// ─────────────────────────────────────────────────────────────
//  src/lib/metrics.js
//  LCA Performance Metrics — Notary scorecard tracking
//  Updates metrics on key events (offer/accept/decline/complete)
//  🏆 Auto-Vetted status system
// ─────────────────────────────────────────────────────────────

import { supabase } from './supabase'

// ── 🏆 VETTED STATUS THRESHOLDS ──────────────────────────────
const VETTED_THRESHOLDS = {
  min_acceptance_rate:  90,    // 90%+ accept rate
  min_completion_rate:  95,    // 95%+ complete rate
  max_avg_response_min: 60,    // 60 min or less to respond
  min_avg_rating:       4.5,   // 4.5 stars or higher
  min_total_signings:   10,    // 10+ career signings
}

// ── 🏆 Check + auto-update Vetted status for a notary ────────
async function checkVettedStatus(notaryId) {
  if (!notaryId) return
  try {
    const { data: n } = await supabase.from('notaries')
      .select(`
        acceptance_rate,
        completion_rate,
        avg_response_minutes,
        avg_rating,
        total_signings,
        is_vetted
      `)
      .eq('id', notaryId).single()

    if (!n) return

    // Check if also has credentials (look at notary_docs count)
    const { count: docCount } = await supabase.from('notary_docs')
      .select('id', { count: 'exact', head: true })
      .eq('notary_id', notaryId)

    // Evaluate Vetted criteria
    const meetsAcceptance  = (n.acceptance_rate || 0) >= VETTED_THRESHOLDS.min_acceptance_rate
    const meetsCompletion  = (n.completion_rate || 0) >= VETTED_THRESHOLDS.min_completion_rate
    const meetsResponse    = n.avg_response_minutes != null && n.avg_response_minutes <= VETTED_THRESHOLDS.max_avg_response_min
    const meetsRating      = (n.avg_rating || 0) >= VETTED_THRESHOLDS.min_avg_rating
    const meetsSignings    = (n.total_signings || 0) >= VETTED_THRESHOLDS.min_total_signings
    const hasCredentials   = (docCount || 0) > 0

    const qualifiesForVetted = meetsAcceptance && meetsCompletion && meetsResponse && meetsRating && meetsSignings && hasCredentials

    // Only PROMOTE automatically — never demote (preserve human dignity)
    if (qualifiesForVetted && !n.is_vetted) {
      await supabase.from('notaries').update({
        is_vetted: true,
        vetted_at: new Date().toISOString(),
        vetted_method: 'auto', // distinguishes from manual admin assignment
      }).eq('id', notaryId)
      console.log('🏆 Notary auto-promoted to VETTED:', notaryId)
      return { promoted: true }
    }

    return { promoted: false, qualifies: qualifiesForVetted }
  } catch(err) {
    console.error('checkVettedStatus error:', err)
    return null
  }
}

// ── Track an offer being made to a notary ────────────────────
export async function trackOffer(notaryId, jobId) {
  if (!notaryId) return
  try {
    // Record offer timestamp on job
    await supabase.from('jobs').update({
      notary_offered_at: new Date().toISOString()
    }).eq('id', jobId)

    // Increment offer counter
    const { data } = await supabase.from('notaries')
      .select('total_offers_received')
      .eq('id', notaryId).single()
    const newCount = (data?.total_offers_received || 0) + 1
    await supabase.from('notaries').update({
      total_offers_received: newCount,
      metrics_updated_at: new Date().toISOString(),
    }).eq('id', notaryId)
  } catch(err) { console.error('trackOffer error:', err) }
}

// ── Track acceptance — update accept count + response time ──
export async function trackAccept(notaryId, jobId) {
  if (!notaryId) return
  try {
    const respondedAt = new Date().toISOString()
    // Record response timestamp on job
    await supabase.from('jobs').update({
      notary_responded_at: respondedAt
    }).eq('id', jobId)

    // Get current notary stats
    const { data: notary } = await supabase.from('notaries')
      .select('total_offers_accepted, total_offers_received, avg_response_minutes')
      .eq('id', notaryId).single()

    const newAccepted = (notary?.total_offers_accepted || 0) + 1
    const received = (notary?.total_offers_received || 0) || 1
    const acceptanceRate = Math.round((newAccepted / received) * 100 * 100) / 100

    // Calculate response time for this job
    const { data: job } = await supabase.from('jobs')
      .select('notary_offered_at')
      .eq('id', jobId).single()

    let newAvgResponse = notary?.avg_response_minutes
    if (job?.notary_offered_at) {
      const offeredAt = new Date(job.notary_offered_at)
      const respondedDate = new Date(respondedAt)
      const responseMinutes = Math.round((respondedDate - offeredAt) / 60000)
      // Rolling average (simple)
      if (newAvgResponse) {
        newAvgResponse = Math.round((newAvgResponse + responseMinutes) / 2)
      } else {
        newAvgResponse = responseMinutes
      }
    }

    await supabase.from('notaries').update({
      total_offers_accepted: newAccepted,
      acceptance_rate: acceptanceRate,
      avg_response_minutes: newAvgResponse,
      metrics_updated_at: new Date().toISOString(),
    }).eq('id', notaryId)

    // 🏆 Check Vetted status after update
    await checkVettedStatus(notaryId)
  } catch(err) { console.error('trackAccept error:', err) }
}

// ── Track decline — update decline counter + acceptance rate ─
export async function trackDecline(notaryId, jobId) {
  if (!notaryId) return
  try {
    const { data: notary } = await supabase.from('notaries')
      .select('total_offers_declined, total_offers_received, total_offers_accepted')
      .eq('id', notaryId).single()

    const newDeclined = (notary?.total_offers_declined || 0) + 1
    const accepted = notary?.total_offers_accepted || 0
    const received = (notary?.total_offers_received || 0) || 1
    const acceptanceRate = Math.round((accepted / received) * 100 * 100) / 100

    await supabase.from('notaries').update({
      total_offers_declined: newDeclined,
      acceptance_rate: acceptanceRate,
      metrics_updated_at: new Date().toISOString(),
    }).eq('id', notaryId)
  } catch(err) { console.error('trackDecline error:', err) }
}

// ── Track completion — update signings + completion rate ────
export async function trackCompletion(notaryId) {
  if (!notaryId) return
  try {
    const { data: notary } = await supabase.from('notaries')
      .select('total_completed, total_offers_accepted, total_signings')
      .eq('id', notaryId).single()

    const newCompleted = (notary?.total_completed || 0) + 1
    const newSignings = (notary?.total_signings || 0) + 1
    const accepted = (notary?.total_offers_accepted || 0) || 1
    const completionRate = Math.round((newCompleted / accepted) * 100 * 100) / 100

    await supabase.from('notaries').update({
      total_completed: newCompleted,
      total_signings: newSignings,
      completion_rate: completionRate,
      last_signing_at: new Date().toISOString(),
      metrics_updated_at: new Date().toISOString(),
    }).eq('id', notaryId)

    // 🏆 Check Vetted status after every completion
    await checkVettedStatus(notaryId)
  } catch(err) { console.error('trackCompletion error:', err) }
}

// ── Get formatted performance summary for display ───────────
export async function getNotaryPerformance(notaryId) {
  if (!notaryId) return null
  try {
    const { data } = await supabase.from('notaries')
      .select(`
        total_offers_received,
        total_offers_accepted,
        total_offers_declined,
        total_signings,
        total_completed,
        acceptance_rate,
        completion_rate,
        avg_response_minutes,
        avg_rating,
        last_signing_at,
        is_vetted,
        vetted_at
      `)
      .eq('id', notaryId).single()

    return data
  } catch(err) {
    console.error('getNotaryPerformance error:', err)
    return null
  }
}

// ── Get Vetted status thresholds (for UI display) ────────────
export function getVettedThresholds() {
  return { ...VETTED_THRESHOLDS }
}

// ── Check if a notary qualifies for Vetted (returns progress) ─
export async function getVettedProgress(notaryId) {
  if (!notaryId) return null
  try {
    const { data: n } = await supabase.from('notaries')
      .select(`
        acceptance_rate,
        completion_rate,
        avg_response_minutes,
        avg_rating,
        total_signings,
        is_vetted
      `)
      .eq('id', notaryId).single()

    if (!n) return null

    const { count: docCount } = await supabase.from('notary_docs')
      .select('id', { count: 'exact', head: true })
      .eq('notary_id', notaryId)

    return {
      is_vetted: n.is_vetted,
      criteria: {
        acceptance: {
          current: n.acceptance_rate || 0,
          required: VETTED_THRESHOLDS.min_acceptance_rate,
          met: (n.acceptance_rate || 0) >= VETTED_THRESHOLDS.min_acceptance_rate,
        },
        completion: {
          current: n.completion_rate || 0,
          required: VETTED_THRESHOLDS.min_completion_rate,
          met: (n.completion_rate || 0) >= VETTED_THRESHOLDS.min_completion_rate,
        },
        response: {
          current: n.avg_response_minutes,
          required: VETTED_THRESHOLDS.max_avg_response_min,
          met: n.avg_response_minutes != null && n.avg_response_minutes <= VETTED_THRESHOLDS.max_avg_response_min,
        },
        rating: {
          current: n.avg_rating || 0,
          required: VETTED_THRESHOLDS.min_avg_rating,
          met: (n.avg_rating || 0) >= VETTED_THRESHOLDS.min_avg_rating,
        },
        signings: {
          current: n.total_signings || 0,
          required: VETTED_THRESHOLDS.min_total_signings,
          met: (n.total_signings || 0) >= VETTED_THRESHOLDS.min_total_signings,
        },
        credentials: {
          current: docCount || 0,
          required: 1,
          met: (docCount || 0) > 0,
        },
      }
    }
  } catch(err) {
    console.error('getVettedProgress error:', err)
    return null
  }
}
